import React, { useEffect, useState } from 'react';
import { removeLastExtension } from '../../../../../helpers/removeExtension';
import {
  containerStyles,
  labelFileNameStyles,
  labelStyles,
  extensionErrorStyles,
  newFilenameInputStyles,
  dropdownWrapperStyles,
  actionsAutocompleteLiStyles,
  actionsAutocompletePlaceholderStyles,
  actionsAutocompleteStyles,
  scrollBlockStyles,
  wholeNewFileStyles,
  newFileCheckboxLabelStyles,
  newFileCheckboxStyles,
  newFileLabelStyles,
  labelFirstFileNameStyles,
  actionsAutocompleteIconStyles
} from './processingMergeJoin.styles';
import { IProcessingMergeJoin } from './processingMergeJoin.model';
import { Autocomplete, TextField } from '@mui/material';
import { ReactComponent as ConcatJoinIcon } from './../../../../../assets/svg/concatJoinIcon.svg';
import { ReactComponent as LeftJoinIcon } from './../../../../../assets/svg/leftJoinIcon.svg';
import { ReactComponent as RightJoinIcon } from './../../../../../assets/svg/rightJoinIcon.svg';
import { ReactComponent as InnerJoinIcon } from './../../../../../assets/svg/innerJoinIcon.svg';
import { ReactComponent as OuterJoinIcon } from './../../../../../assets/svg/outerJoinIcon.svg';
import { AddPreprocessButton } from '../../../../../components/Buttons/addPreprocessButton';
import { makeStyles } from '@mui/styles';
import { IBlock } from '../../../processingPreprocess/processingPreprocess.models';
import ProcessingMergeJoinInnerBlock from './processingMergeJoinInnerBlock/processingMergeJoinInnerBlock.component';
import { dispatch, useAppSelector } from '../../../../../store/hooks';
import { previewsMergeMiddleware, previewsMergeSelector } from '../../../../../store/slices/previewsMerge';
import { resourcesSelector } from '../../../../../store/slices/resources';
import { processingMergeJoinEmptyBlock } from './processingMergeJoinInnerBlock/processingMergeJoinInnerBlock.constants';
import API from '../../../../../manager/API';
import { modelVisualisationsMiddleware } from '../../../../../store/slices/modelVisualisations';
import { summaryMiddleware } from '../../../../../store/slices/summary';

const useStyles = makeStyles({
  root: {
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    scrollbarWidth: 'none', // Hide scrollbar in Firefox
    '-ms-overflow-style': 'none' // Hide scrollbar in IE and Edge
  }
});

const ProcessingMergeJoin: React.FC<IProcessingMergeJoin> = ({
  isDuplicateChecked,
  setIsDuplicateChecked,
  onClose,
  data,
  setData,
  selectedMergeJoinOption,
  setSelectedMergeJoinOption,
  extensionError,
  setExtensionError,
  summaryData,
  processingMergeInputData,
  processingMergeNewFileNameValue,
  setProcessingMergeNewFileNameValue,
  processingMergeJoinBlocks,
  setProcessingMergeJoinBlocks
}) => {
  const classes = useStyles();

  const [errorMessage, setErrorMessage] = useState('');
  const [isPlusDisabled, setIsPlusDisabled] = useState(false);

  const { previewsMergeData } = useAppSelector(previewsMergeSelector.previewsMergeData);
  const { resourcesData } = useAppSelector(resourcesSelector.resourcesData);
  const getFileId = (fileName: string) => {
    const file = resourcesData.find((item: any) => item.file_name === fileName);
    return file ? file.file_id : null;
  };
  const previewsMergeURL = `/data_operations/get_previews_for_mergeing?first_dataset_id=${
    data.file_id
  }&second_dataset_id=${getFileId(
    processingMergeInputData.dataset.length > 0
      ? `${processingMergeInputData.dataset}.csv`
      : processingMergeInputData?.uploadedFileName
  )}`;

  const joinOptions = [
    { id: 'concat', label: 'concat', icon: <ConcatJoinIcon /> },
    { id: 'left', label: 'left join', icon: <LeftJoinIcon /> },
    { id: 'right', label: 'right join', icon: <RightJoinIcon /> },
    { id: 'inner', label: 'inner join', icon: <InnerJoinIcon /> },
    { id: 'outer', label: 'outer join', icon: <OuterJoinIcon /> }
  ];

  const handleNewFileNameCheckboxChange = () => {
    setIsDuplicateChecked(!isDuplicateChecked);
  };

  const newFileNameValueChange = (event: any) => {
    const value = event.target.value;

    const validPattern = /^[a-zA-Z0-9_-]*$/;

    if (validPattern.test(value)) {
      setProcessingMergeNewFileNameValue(value);
      if (value.startsWith('-') || value.startsWith('_') || value.endsWith('-') || value.endsWith('_')) {
        setExtensionError(true);
        setErrorMessage('The dataset name cannot start and end with a special character.');
      } else {
        setExtensionError(false);
        setErrorMessage('');
      }
    } else {
      setProcessingMergeNewFileNameValue(value);
      setExtensionError(true);
      setErrorMessage('Spaces or special symbols are not allowed.');
    }
  };

  const onPlusClick = () => {
    if (!processingMergeJoinBlocks.length) {
      setProcessingMergeJoinBlocks([{ id: 0, singleActionBody: {} }]);
    } else if (processingMergeJoinBlocks.length < 10) {
      setProcessingMergeJoinBlocks((prev: any) => [
        ...prev,
        { id: processingMergeJoinBlocks[processingMergeJoinBlocks.length - 1].id + 1, singleActionBody: {} }
      ]);
    }
  };
  const secondDatasetClick = () => {
    onClose();
    const getSummaryUrl = `/data_operations/data_summary?file_name=${
      processingMergeInputData.dataset.length > 0
        ? `${processingMergeInputData.dataset}.csv`
        : processingMergeInputData?.uploadedFileName
    }`;
    const getPreviewUrl = `/data_operations/file_preview?filename=${
      processingMergeInputData.dataset.length > 0
        ? `${processingMergeInputData.dataset}.csv`
        : processingMergeInputData?.uploadedFileName
    }`;
    const fetchPreviewData = async () => {
      try {
        const response = await API.preview.getPreview(getPreviewUrl);
        if (!response) {
          throw new Error('Failed to fetch preview data');
        }
        if (response.data && response.data.file_id) {
          const getVisualisationsUrl = `/visualisations/get_visualisations?id=${response.data.file_id}`;
          dispatch(modelVisualisationsMiddleware.getModelVisualisations(getVisualisationsUrl));
        }
        setData(response.data);
      } catch (error) {
        console.error('Error fetching preview data:', error);
      } finally {
      }
    };

    fetchPreviewData();
    dispatch(summaryMiddleware.getSummary(getSummaryUrl));
  };

  const updateSingleActionBody = (blocks: IBlock[], targetBlockId: number, newSingleActionBody: any) => {
    return blocks.map((block) => {
      if (block.id === targetBlockId) {
        return {
          ...block,
          singleActionBody: { ...newSingleActionBody } // Update the block's singleActionBody
        };
      }
      return block;
    });
  };

  const handleOptionsExhausted = (disabled: boolean) => {
    setIsPlusDisabled(disabled);
  };

  const deleteBlock = (blockId: number) => {
    setProcessingMergeJoinBlocks((prevBlocks: IBlock[]) => {
      const updatedBlocks = prevBlocks.filter((block) => block.id !== blockId);
      return updatedBlocks; // Just return the filtered blocks without changing their ids
    });
  };

  useEffect(() => {
    dispatch(previewsMergeMiddleware.previewsMerge(previewsMergeURL));
  }, []);

  useEffect(() => {
    setProcessingMergeJoinBlocks([processingMergeJoinEmptyBlock({ id: 1 })]);
  }, [selectedMergeJoinOption]);

  return (
    <div style={containerStyles}>
      <span style={labelStyles}>
        Merging <span style={labelFirstFileNameStyles}>{removeLastExtension(summaryData.filename)}</span> with{' '}
        <span style={labelFileNameStyles} onClick={secondDatasetClick}>
          {processingMergeInputData.dataset.length > 0
            ? removeLastExtension(processingMergeInputData.dataset)
            : removeLastExtension(processingMergeInputData?.uploadedFileName)}
        </span>
      </span>
      <div>
        <input
          style={newFilenameInputStyles}
          onChange={newFileNameValueChange}
          placeholder="New dataset name"
          value={processingMergeNewFileNameValue}
        />
        {extensionError && <p style={extensionErrorStyles}>{errorMessage}</p>}
      </div>
      <div style={dropdownWrapperStyles}>
        <Autocomplete
          value={selectedMergeJoinOption}
          options={joinOptions}
          getOptionLabel={(option) => option.label}
          onChange={(event, newValue) => {
            if (newValue) {
              setSelectedMergeJoinOption({
                id: newValue.id,
                label: newValue.label
              });
            } else {
              setSelectedMergeJoinOption(null);
            }
          }}
          // Set `disableClearable` to false to ensure the clear button is available when a selection is made
          disableClearable={false}
          sx={actionsAutocompleteStyles}
          renderOption={(props, option) => (
            <li {...props} style={actionsAutocompleteLiStyles}>
              <span style={actionsAutocompleteIconStyles}>{option.icon}</span> {/* Apply the icon styles */}
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select joining method"
              label=""
              InputProps={{
                ...params.InputProps,
                style: actionsAutocompleteLiStyles
              }}
              sx={actionsAutocompletePlaceholderStyles}
            />
          )}
        />
      </div>
      {selectedMergeJoinOption?.label.length > 0 && (
        <div style={wholeNewFileStyles}>
          <div style={newFileCheckboxLabelStyles}>
            <input
              type="checkbox"
              checked={isDuplicateChecked}
              onChange={handleNewFileNameCheckboxChange}
              style={newFileCheckboxStyles}
            />
            {selectedMergeJoinOption?.label === 'concat' && (
              <label style={newFileLabelStyles}>Drop duplicates from the merged dataset after concat</label>
            )}
            {selectedMergeJoinOption?.label === 'left join' && (
              <label style={newFileLabelStyles}>Drop duplicates from the right dataset before joining</label>
            )}
            {selectedMergeJoinOption?.label === 'right join' && (
              <label style={newFileLabelStyles}>Drop duplicates from the left dataset before joining</label>
            )}
            {selectedMergeJoinOption?.label === 'inner join' && (
              <label style={newFileLabelStyles}>Drop duplicates from both datasets before joining</label>
            )}
            {selectedMergeJoinOption?.label === 'outer join' && (
              <label style={newFileLabelStyles}>Drop duplicates from both datasets before joining</label>
            )}
          </div>
        </div>
      )}
      {selectedMergeJoinOption?.label.length > 0 && selectedMergeJoinOption?.label !== 'concat' && (
        <div className={classes.root} style={scrollBlockStyles}>
          {processingMergeJoinBlocks.map((block, index) => (
            <div key={block.id}>
              <ProcessingMergeJoinInnerBlock
                setIsDuplicateChecked={setIsDuplicateChecked}
                selectedMergeJoinOption={selectedMergeJoinOption}
                updateSingleActionBody={updateSingleActionBody}
                data={previewsMergeData}
                summaryData={summaryData}
                processingMergeInputData={processingMergeInputData}
                setBlocks={setProcessingMergeJoinBlocks}
                block={block}
                blocks={processingMergeJoinBlocks}
                element={block.id}
                deleteBlock={deleteBlock}
                onOptionsExhausted={handleOptionsExhausted}
              />
            </div>
          ))}
          {processingMergeJoinBlocks.length <= 9 && (
            <AddPreprocessButton
              isMerge
              convert={false}
              processingMergeJoinBlocks={processingMergeJoinBlocks}
              handlePlusButtonClick={onPlusClick}
              title="Add Columns"
              isPlusDisabled={isPlusDisabled}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProcessingMergeJoin;
